// File: src/POA1Page.js 
// OOOSH Driver Verification - Proof of Address #1 Upload Page 
// Uses Claude Vision API for validation with real-time preview
// v3.0: Oct 2026 redesign — render restyled onto StepShell; upload/validation logic unchanged
// v2.9: Added heic2any lazy-loaded conversion for iPhone HEIC photos
// v2.8: Fixed JPG regression - reverted to FileReader for image reading, added HEIC detection
// v2.7: Fixed blank page when file format not supported (HEIC etc.) - error + retry now always visible
// v2.6: Saves POA1 address to sessionStorage for POA2 comparison in mismatch mode
// v2.5: Added addressMismatch prop for address mismatch flow
// Updated: February 2026

import React, { useState, useCallback, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import reportError from './utils/errorReporter';
import StuckDriverHelper from './StuckDriverHelper';
import { StepShell, PHASE, Card, PrimaryButton, Note, Spinner } from './ui/HireFormUI';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// What the insurer accepts — the list this page has always shown.
const ACCEPTED_DOCUMENTS = [
  'Gas, electric or water bill',
  'Broadband or phone bill',
  'Bank or building society statement',
  'Credit card statement',
  'Council tax bill',
  'Mortgage statement',
  'Tenancy agreement',
  'TV licence letter',
  'HMRC, DWP, NHS or DVLA letter',
];

const jobIdFromUrl = () => {
  try {
    return new URLSearchParams(window.location.search).get('job');
  } catch (e) {
    return null;
  }
};

// Declared at module scope, never inside the page (see CLAUDE.md).
const WhatCounts = () => (
  <Card className="hf-card-tight">
    <h3 className="hf-h3">What counts</h3>
    <p className="hf-body">
      Dated in the <strong>last 90 days</strong>, from <strong>two different places</strong>{' '}
      (so not two statements from the same bank), showing your name and current address.
    </p>
    <div className="hf-chips">
      {ACCEPTED_DOCUMENTS.map((doc) => (
        <span key={doc} className="hf-chip">{doc}</span>
      ))}
    </div>
    <Note tone="amber">Insurance documents (car, home, travel) can't be used.</Note>
  </Card>
);

const POA1Page = ({ email, driverName, licenseAddress, existingPoa2Provider, addressMismatch, onComplete, onBack, jobId }) => {
  const resolvedJobId = jobId || jobIdFromUrl();
  // State management
  // uploadState: idle, uploading, converting, processing, saving, validated, validated-with-concerns, error
  const [uploadState, setUploadState] = useState('idle');
  const [previewImage, setPreviewImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [failureCount, setFailureCount] = useState(0);
  const [concernNotes, setConcernNotes] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const fileInputRef = useRef(null);

  // Compress image to reduce file size for Claude Vision API (5MB limit)
  // Only called when files exceed 3.5MB - uses Canvas which can decode standard JPG/PNG
  const compressImage = async (base64Data, maxWidth = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if wider than maxWidth
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to JPEG with compression
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        const compressedBase64 = compressedDataUrl.split(',')[1];
        
        console.log(`📦 Image compressed: ${Math.round(base64Data.length / 1024)}KB → ${Math.round(compressedBase64.length / 1024)}KB`);
        
        resolve(compressedBase64);
      };
      img.src = `data:image/jpeg;base64,${base64Data}`;
    });
  };

  // Convert PDF to image using PDF.js
  const convertPdfToImage = async (pdfFile) => {
    const arrayBuffer = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    
    const scale = 2;
    const viewport = page.getViewport({ scale });
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;
    
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    return dataUrl.split(',')[1];
  };

  // v2.8: Detect HEIC format from file properties and magic bytes
  // iPhones save photos as HEIC by default - browsers can't decode this format
  const isHeicFile = async (file) => {
    // Check MIME type and file extension first (quick check)
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    if (type.includes('heic') || type.includes('heif') || name.endsWith('.heic') || name.endsWith('.heif')) {
      console.log('📱 HEIC detected from file type/extension');
      return true;
    }
    
    // Check magic bytes: HEIC/HEIF files have 'ftyp' at offset 4-7
    // This catches cases where iPhone labels HEIC files with .jpg extension
    try {
      const slice = file.slice(0, 12);
      const buffer = await slice.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      // 'ftyp' at bytes 4-7 (0x66=f, 0x74=t, 0x79=y, 0x70=p)
      if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
        const subtype = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11]);
        const heicVariants = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1'];
        if (heicVariants.includes(subtype)) {
          console.log(`📱 HEIC detected from magic bytes (subtype: ${subtype})`);
          return true;
        }
      }
    } catch (e) {
      // If we can't read magic bytes, rely on type/extension check above
      console.warn('⚠️ Could not read file magic bytes for HEIC detection');
    }
    return false;
  };

  // v2.9: Convert HEIC file to JPEG using heic2any (lazy-loaded)
  // Only downloads the heic2any library when actually needed
  const convertHeicToJpeg = async (file) => {
    console.log('📱 Loading HEIC converter...');
    
    // Lazy-load heic2any - only downloads when a HEIC file is actually detected
    const heic2any = (await import('heic2any')).default;
    
    console.log('📱 Converting HEIC to JPEG...');
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92
    });
    
    // heic2any can return an array of blobs (for multi-image HEIC) or a single blob
    const resultBlob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    
    console.log(`📱 HEIC converted to JPEG: ${Math.round(resultBlob.size / 1024)}KB`);
    return resultBlob;
  };

  // v2.8: Read file as base64 using FileReader
  // This just reads raw bytes - doesn't try to decode/re-encode the image
  // Works reliably for all standard image formats (JPG, PNG, WebP, GIF)
  // Also works with Blob objects (e.g. from heic2any conversion)
  const readFileAsBase64 = (fileOrBlob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // FileReader gives us data:<type>;base64,XXXX - extract the base64 part
        const base64 = reader.result.split(',')[1];
        console.log(`📄 File read as base64: ${Math.round(base64.length * 0.75 / 1024)}KB`);
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to read file. Please try again.'));
      reader.readAsDataURL(fileOrBlob);
    });
  };

  // Save document and metadata to Monday.com
  const saveDocumentAndMetadata = useCallback(async (base64Data, validationData) => {
    setUploadState('saving');
    const approxBytes = Math.round(base64Data.length * 0.75);
    console.log(`📁 Uploading POA1 file to Monday.com... (~${Math.round(approxBytes / 1024)}KB)`);

    // v2.7: Retry once on failure, log response details loudly, report to
    // telemetry. Silently swallowing here previously meant a POA file could
    // vanish between the browser and OP's R2 with no user-visible warning and
    // no server log to correlate.
    const attemptUpload = async (attempt) => {
      const uploadResponse = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upload-file-board-a',
          email: email,
          fileType: 'poa1',
          fileData: base64Data,
          filename: `poa1_${Date.now()}.jpg`,
          clearExisting: true
        })
      });

      if (uploadResponse.ok) {
        console.log(`✅ POA1 file uploaded (attempt ${attempt})`);
        return true;
      }

      const bodyText = await uploadResponse.text().catch(() => '');
      console.error(`⚠️ POA1 upload attempt ${attempt} failed: HTTP ${uploadResponse.status} — ${bodyText.slice(0, 500)}`);
      return { status: uploadResponse.status, body: bodyText };
    };

    let uploadOk = false;
    let lastFailure = null;
    try {
      const first = await attemptUpload(1);
      if (first === true) {
        uploadOk = true;
      } else {
        lastFailure = first;
        const retryable = !first.status || first.status === 429 || first.status >= 500;
        if (retryable) {
          await new Promise(r => setTimeout(r, 1500));
          const second = await attemptUpload(2);
          if (second === true) {
            uploadOk = true;
          } else {
            lastFailure = second;
          }
        }
      }
    } catch (uploadError) {
      console.error('⚠️ POA1 upload threw:', uploadError);
      lastFailure = { status: 0, body: uploadError?.message || String(uploadError) };
    }

    if (!uploadOk) {
      try {
        fetch('/.netlify/functions/log-client-error', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 'POA1Page',
            error: `poa1 upload failed: HTTP ${lastFailure?.status} — ${(lastFailure?.body || '').slice(0, 300)}`,
            severity: 'critical',
            email: email,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            additionalInfo: `approxBytes=${approxBytes}`,
          }),
        }).catch(() => {});
      } catch (_) {
        // best effort
      }
    }

    // Update POA1 metadata (validUntil, provider, review status)
    try {
      await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update-driver-board-a',
          email: email,
          updates: {
            poa1ValidUntil: validationData.result.validUntil,
            poa1Provider: validationData.result.extractedData?.provider || 'Unknown',
            poa1ReviewStatus: uploadOk
              ? (validationData.result.reviewStatus || 'Verified')
              : 'Manual review needed',
            homeAddress: validationData.result.extractedData?.address?.full || ''
          }
        })
      });
      console.log('✅ POA1 metadata saved');
    } catch (metadataError) {
      console.error('⚠️ Metadata save error:', metadataError);
    }
    
    // v2.6: Save POA1 data to sessionStorage for POA2 to use in mismatch mode
    if (addressMismatch && validationData.result?.extractedData) {
      try {
        const poa1Data = {
          provider: validationData.result.extractedData.provider,
          address: validationData.result.extractedData.address,
          documentDate: validationData.result.extractedData.documentDate
        };
        sessionStorage.setItem('poa1Data', JSON.stringify(poa1Data));
        console.log('💾 POA1 data saved to sessionStorage for POA2 comparison');
      } catch (e) {
        console.warn('⚠️ Could not save POA1 data to sessionStorage:', e);
      }
    }
  }, [email, addressMismatch]);

  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;
    
    console.log('📄 File selected:', file.name, file.type, `${Math.round(file.size / 1024)}KB`);
    setUploadState('uploading');
    setErrorMessage(null);
    setValidationResult(null);
    setExtractedData(null);
    setConcernNotes(null);
    
    try {
      let base64Data;
      
      // Handle PDF files - convert to image first
      if (file.type === 'application/pdf') {
        console.log('📄 Converting PDF to image...');
        base64Data = await convertPdfToImage(file);
      } else if (file.type.startsWith('image/')) {
        // v2.8: Check for HEIC format first (iPhone default - browsers can't decode it)
        if (await isHeicFile(file)) {
          // v2.9: Convert HEIC to JPEG using heic2any (lazy-loaded)
          console.log('📱 HEIC file detected - converting to JPEG...');
          setUploadState('converting');  // Show specific "converting" UI message
          
          try {
            const jpegBlob = await convertHeicToJpeg(file);
            base64Data = await readFileAsBase64(jpegBlob);
            console.log('✅ HEIC successfully converted to JPEG');
          } catch (heicError) {
            console.error('❌ HEIC conversion failed:', heicError);
            // Fall back to helpful error message if conversion fails
            throw new Error(
              'We couldn\'t convert your iPhone photo automatically. Please try one of these instead:\n\n' +
              '• Take a screenshot of the document on your screen\n' +
              '• Open the photo in Photos, tap Share, then "Save as JPG"\n' +
              '• Upload a PDF version of the document if you have one'
            );
          }
        } else {
          // v2.8: Read image as raw base64 via FileReader
          // This just reads the bytes - doesn't try to decode or re-encode
          // Works reliably for JPG, PNG, WebP, GIF, BMP, and any browser-supported format
          console.log('📄 Reading image file...');
          base64Data = await readFileAsBase64(file);
        }
      } else {
        throw new Error('Please upload a PDF or image file (JPG, PNG)');
      }
      
      // Compress if over 3.5MB (Claude limit is 5MB, leave headroom for safety)
      const sizeInBytes = base64Data.length * 0.75; // Base64 is ~33% larger than binary
      if (sizeInBytes > 3.5 * 1024 * 1024) {
        console.log('📦 Image too large, compressing (pass 1)...');
        base64Data = await compressImage(base64Data, 1600, 0.8);
        
        // Second pass if still too large
        let newSize = base64Data.length * 0.75;
        if (newSize > 3.5 * 1024 * 1024) {
          console.log('📦 Still too large, compressing (pass 2)...');
          base64Data = await compressImage(base64Data, 1200, 0.7);
          
          // Third pass if still too large
          newSize = base64Data.length * 0.75;
          if (newSize > 3.5 * 1024 * 1024) {
            console.log('📦 Still too large, compressing (pass 3)...');
            base64Data = await compressImage(base64Data, 1000, 0.6);
            
            // Final safety check - if still over 4.5MB, show friendly error
            newSize = base64Data.length * 0.75;
            if (newSize > 4.5 * 1024 * 1024) {
              throw new Error('Your image is too large even after compression. Please try taking a photo at a lower resolution or uploading a smaller file.');
            }
          }
        }
      }
      
      setPreviewImage(base64Data);
      setUploadState('processing');
      
      // Send to Claude Vision for validation
      console.log('🤖 Sending to Claude Vision for validation...');
      if (existingPoa2Provider) {
        console.log('📋 Existing POA2 is from:', existingPoa2Provider);
      }
      if (addressMismatch) {
        console.log('🏠 Address mismatch mode - skipping licence address comparison');
      } else if (licenseAddress) {
        console.log('📍 Comparing against licence address:', licenseAddress);
      } else {
        console.log('📍 No licence address available - address comparison will be skipped');
      }
      
      const validationResponse = await fetch('/.netlify/functions/validate-poa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate-poa1',
          imageData: base64Data,
          email: email,
          expectedName: driverName,
          expectedAddress: licenseAddress || null,
          existingPoa2Provider: existingPoa2Provider || null,
          addressMismatch: addressMismatch || false
        })
      });
      
      if (!validationResponse.ok) {
        throw new Error('Validation service unavailable. Please try again.');
      }
      
      const validationData = await validationResponse.json();
      console.log('✅ Validation result:', validationData);
      console.log('🔍 Concern level:', validationData.result?.concernLevel);
      
      setExtractedData(validationData.result?.extractedData || null);
      setValidationResult(validationData.result);
      setConcernNotes(validationData.result?.concernNotes || null);
      
      if (validationData.result?.isValid) {
        // Check if there are minor concerns
        if (validationData.result?.concernLevel === 'minor') {
          // Valid but with concerns - show choice to user
          setUploadState('validated-with-concerns');
          console.log('⚠️ Document valid but flagged for manual review:', validationData.result?.concernNotes);
        } else {
          // Clean validation - proceed to save
          await saveDocumentAndMetadata(base64Data, validationData);
          setUploadState('validated');
        }
      } else {
        setUploadState('error');
        setErrorMessage(validationData.result?.rejectionReason || 'Document could not be validated');
      }
       
    } catch (error) {
      console.error('❌ Upload/validation error:', error);
      reportError({ page: 'POA1Page', error: error.message, severity: 'warning', email }); 
      setFailureCount(prev => prev + 1);
      setUploadState('error');
      setErrorMessage(error.message || 'Failed to process document');
    }
  }, [email, driverName, licenseAddress, existingPoa2Provider, addressMismatch, saveDocumentAndMetadata]);

  // Handle "Continue anyway" for documents with minor concerns
  const handleContinueWithConcerns = useCallback(async () => {
    console.log('👤 User chose to continue with flagged document');
    
    // Save the document with "Manual review needed" status
    const modifiedValidationData = {
      result: {
        ...validationResult,
        reviewStatus: 'Manual review needed'
      }
    };
    
    await saveDocumentAndMetadata(previewImage, modifiedValidationData);
    setUploadState('validated');
  }, [validationResult, previewImage, saveDocumentAndMetadata]);

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  // Reset to try again
  const handleReset = () => {
    setUploadState('idle');
    setPreviewImage(null);
    setExtractedData(null);
    setValidationResult(null);
    setErrorMessage(null);
    setConcernNotes(null);
  };

  // Continue to next step
  const handleContinue = () => {
    if (validationResult?.isValid && extractedData) {
      onComplete({
        provider: extractedData.provider,
        documentDate: extractedData.documentDate,
        address: extractedData.address,
        validUntil: validationResult.validUntil
      });
    }
  };

  // Render status icon (a tick, cross or question mark in a small circle)
  const renderStatusIcon = (isValid) => {
    if (isValid === true) {
      return <span className="hf-mark is-ok" aria-label="OK">✓</span>;
    } else if (isValid === false) {
      return <span className="hf-mark is-bad" aria-label="Problem">✕</span>;
    }
    return <span className="hf-mark is-unknown" aria-label="Not checked">?</span>;
  };

  const isBusy = uploadState === 'uploading' || uploadState === 'converting' || uploadState === 'processing' || uploadState === 'saving';
  const showsPreview = (uploadState === 'validated' || uploadState === 'validated-with-concerns' || (uploadState === 'error' && previewImage)) && previewImage;

  return (
    <StepShell jobId={resolvedJobId} phase={PHASE.ADDRESS}>
      <StuckDriverHelper failureCount={failureCount} pageName="POA1Page" email={email} />

      <Card>
        <div>
          <h2 className="hf-h2">Proof of address 1 of 2</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>
            A recent bill, statement or official letter showing your name and current address.
          </p>
        </div>

        {/* Address Mismatch Mode Info Banner */}
        {addressMismatch && (
          <Note tone="accent">
            <strong>New address.</strong> Your letters show a different address from your driving licence, so your two proofs of address need to match <strong>each other</strong>. We'll also ask for a passport check.
          </Note>
        )}

        {/* POA2 Provider Warning (for returning drivers with valid POA2) */}
        {existingPoa2Provider && (
          <Note tone="amber">
            <strong>Different provider required.</strong> Your other proof of address is from <strong>{existingPoa2Provider}</strong>, so this one must come from a different company.
          </Note>
        )}

        {/* Upload Area */}
        {uploadState === 'idle' && (
          <div
            className={`hf-upload${isDragging ? ' is-dragging' : ''}`}
            role="button"
            tabIndex={0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              style={{ display: 'none' }}
            />
            <span className="hf-upload-plus" aria-hidden="true">+</span>
            <div className="hf-upload-main">
              <p className="hf-upload-title">
                {isDragging ? 'Drop it here' : 'Upload proof of address 1'}
              </p>
              <p className="hf-upload-sub">Take a photo or choose a file</p>
              <p className="hf-upload-meta">PDF, JPG or PNG</p>
            </div>
          </div>
        )}

        {/* Processing State - v2.9: 'converting' state for HEIC conversion */}
        {isBusy && (
          <div className="hf-busy-panel" role="status">
            <Spinner />
            <p className="hf-busy-title">
              {uploadState === 'uploading' && 'Uploading your document…'}
              {uploadState === 'converting' && 'Converting your iPhone photo…'}
              {uploadState === 'processing' && 'Reading your document…'}
              {uploadState === 'saving' && 'Saving to your record…'}
            </p>
            <p className="hf-small">
              {uploadState === 'converting'
                ? 'Your photo is in a format that needs converting. This can take a few seconds.'
                : 'This can take a few seconds. Please keep this page open.'}
            </p>
          </div>
        )}

        {/* v2.7: Error state WITHOUT preview image (e.g. unsupported file format) */}
        {/* This handles the case where file processing fails BEFORE a preview is generated */}
        {uploadState === 'error' && !previewImage && (
          <>
            <Note tone="error">
              <strong>We couldn't read this file.</strong>{' '}
              <span className="hf-pre-line">{errorMessage}</span>
            </Note>

            {/* Helpful suggestions */}
            <div className="hf-inset">
              <p className="hf-small hf-strong" style={{ marginBottom: 6 }}>Things to try</p>
              <ul className="hf-bullets">
                <li>If you have the document as a <strong>PDF</strong>, upload that instead</li>
                <li>Take a <strong>screenshot</strong> of the document on your screen</li>
                <li>On an iPhone, open the photo and share it as a <strong>JPG</strong> before uploading</li>
                <li>Take a <strong>new photo</strong> of the paper document</li>
              </ul>
            </div>
          </>
        )}

        {/* Preview with Validation Results (error WITH preview, or success states) */}
        {showsPreview && (
          <>
            {uploadState === 'validated' && (
              <div className="hf-upload is-done">
                <span className="hf-upload-tick" aria-hidden="true">✓</span>
                <div className="hf-upload-main">
                  <p className="hf-upload-title">Proof of address 1 checked</p>
                  <p className="hf-upload-done-sub">
                    {extractedData?.provider
                      ? `${extractedData.provider} · saved to your record`
                      : 'Saved to your record'}
                  </p>
                </div>
              </div>
            )}

            {/* Minor concerns: valid, but the driver chooses whether to go on with it */}
            {uploadState === 'validated-with-concerns' && (
              <Note tone="amber">
                <strong>Our check flagged something on this document.</strong>{' '}
                {concernNotes && <>{concernNotes} </>}
                You can carry on with it and our team will check it by hand, or upload a different document instead.
              </Note>
            )}

            {/* Why it was rejected (validation failed but we have a preview) */}
            {uploadState === 'error' && (
              <Note tone="error">
                <strong>We couldn't accept this document.</strong>{' '}
                {errorMessage && <span className="hf-pre-line">{errorMessage}</span>}
              </Note>
            )}

            {/* Document Preview */}
            <div className="hf-doc-preview">
              <img
                src={`data:image/jpeg;base64,${previewImage}`}
                alt="Your uploaded document"
              />
            </div>

            {/* Extracted Data Display */}
            {extractedData && (
              <div className="hf-doc-rows">
                {/* v2.6: Document type icon based on documentType validity, not overall isValidDocument */}
                <div className="hf-doc-row">
                  {renderStatusIcon(extractedData.documentType !== 'invalid' && extractedData.documentType !== 'insurance')}
                  <span className="hf-doc-row-label">Document</span>
                  <span className="hf-doc-row-value">
                    {extractedData.documentTypeDescription || extractedData.documentType || 'Unknown'}
                  </span>
                </div>

                <div className="hf-doc-row">
                  {renderStatusIcon(!!extractedData.provider && !extractedData.providerDuplicate)}
                  <span className="hf-doc-row-label">From</span>
                  <span className="hf-doc-row-value">
                    {extractedData.provider || 'Not detected'}
                    {extractedData.providerDuplicate && (
                      <span className="hf-doc-row-note hf-text-bad">
                        Same as your other proof of address
                      </span>
                    )}
                    {extractedData.provider && !extractedData.providerDuplicate && existingPoa2Provider && (
                      <span className="hf-doc-row-note hf-text-ok">
                        Different provider
                      </span>
                    )}
                  </span>
                </div>

                <div className="hf-doc-row">
                  {renderStatusIcon(extractedData.nameMatches !== false)}
                  <span className="hf-doc-row-label">Name</span>
                  <span className="hf-doc-row-value">
                    {extractedData.recipientName || 'Not detected'}
                  </span>
                </div>

                <div className="hf-doc-row">
                  {renderStatusIcon(extractedData.dateAge !== undefined && extractedData.dateAge <= 90)}
                  <span className="hf-doc-row-label">Date</span>
                  <span className="hf-doc-row-value">
                    {extractedData.documentDate || 'Not detected'}
                    {extractedData.dateAge !== undefined && (
                      <span className={`hf-doc-row-note ${extractedData.dateAge <= 90 ? 'hf-text-ok' : 'hf-text-bad'}`}>
                        {extractedData.dateAge} days ago
                      </span>
                    )}
                  </span>
                </div>

                <div className="hf-doc-row">
                  {renderStatusIcon(
                    extractedData.addressMatches === true ? true :
                    extractedData.addressMatches === false ? false :
                    extractedData.addressComparisonSkipped ? null :
                    !!extractedData.address?.postcode
                  )}
                  <span className="hf-doc-row-label">Address</span>
                  <span className="hf-doc-row-value">
                    {extractedData.address?.full || 'Not detected'}
                    {extractedData.addressMatches === false && (
                      <span className="hf-doc-row-note hf-text-bad">
                        Doesn't match the address we expected
                      </span>
                    )}
                    {extractedData.addressComparisonSkipped && (
                      <span className="hf-doc-row-note hf-text-warn">
                        We couldn't compare this address
                      </span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Action Buttons */}
      {uploadState === 'error' && !previewImage && (
        <div className="hf-actions">
          <PrimaryButton onClick={handleReset}>Try again</PrimaryButton>
          {onBack && (
            <button type="button" className="hf-btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
        </div>
      )}

      {showsPreview && (
        <div className="hf-actions">
          {/* For validated-with-concerns: show two options */}
          {uploadState === 'validated-with-concerns' && (
            <>
              <PrimaryButton onClick={handleContinueWithConcerns}>
                Continue with this document
              </PrimaryButton>
              <button type="button" className="hf-btn-secondary" onClick={handleReset}>
                Upload a different document
              </button>
            </>
          )}

          {uploadState === 'validated' && (
            <>
              <PrimaryButton onClick={handleContinue}>
                {/* existingPoa2Provider is only passed when POA2 is still valid —
                    then the router skips POA2 and goes on to the final checks. */}
                {existingPoa2Provider ? 'Continue to final checks' : 'Continue to proof of address 2'}
              </PrimaryButton>
              <button type="button" className="hf-btn-secondary" onClick={handleReset}>
                Try a different document
              </button>
            </>
          )}

          {uploadState === 'error' && (
            <PrimaryButton onClick={handleReset}>
              Try a different document
            </PrimaryButton>
          )}
        </div>
      )}

      {uploadState !== 'validated' && <WhatCounts />}

      {/* Back Button (only when idle) */}
      {uploadState === 'idle' && onBack && (
        <div className="hf-center-link">
          <button type="button" className="hf-link-btn" onClick={onBack}>
            Back
          </button>
        </div>
      )}
    </StepShell>
  );
};

export default POA1Page;
